import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
 
// ─── Module-level fetch interceptor ──────────────────────────────────────────
// Must live here so it wraps window.fetch before ApiFetcher or any other
// module captures a reference to it.
 
(function interceptCartFetch() {
  if (window.__cartInventoryIntercepted) return;
  window.__cartInventoryIntercepted = true;
 
  const _fetch = window.fetch.bind(window);
 
  window.fetch = async function (input, init, ...rest) {
    const url = typeof input === 'string' ? input : (input?.url ?? '');
 
    if (!url.includes('/cart/add')) {
      return _fetch(input, init, ...rest);
    }
 
    // ── Parse requested quantity from the request body ──────────────────────
    let requestedQty = null;
    try {
      const body = init?.body;
      if (typeof body === 'string') {
        try {
          const parsed = JSON.parse(body);
          requestedQty =
            parsed?.quantity ??
            parsed?.items?.[0]?.quantity ??
            null;
        } catch {
          const match = body.match(/quantity=(\d+)/);
          if (match) requestedQty = parseInt(match[1], 10);
        }
      } else if (body instanceof URLSearchParams) {
        requestedQty = parseInt(body.get('quantity') ?? '', 10) || null;
      } else if (body instanceof FormData) {
        requestedQty = parseInt(String(body.get('quantity') ?? ''), 10) || null;
      }
    } catch { /* silent */ }
 
    const response = await _fetch(input, init, ...rest);
 
    // ── Handle 200 (capped) and 422 (rejected) responses ───────────────────
    try {
      const cloned = response.clone();
      const text   = await cloned.text();
      const data   = JSON.parse(text);
 
      if (response.ok) {
        // The response quantity is the line-item total after this add.
        const lineQty =
          data?.quantity ??
          data?.items?.reduce((sum, item) => sum + item.quantity, 0) ??
          null;
 
        if (requestedQty !== null && lineQty !== null && lineQty < requestedQty) {
          document.dispatchEvent(
            new CustomEvent('cart-inventory:limit', {
              detail: { requested: requestedQty, added: lineQty },
            })
          );
        }
      } else if (response.status === 422) {
        // Shopify error body: { "description": "...solo se añadieron 12 artículos..." }
        const description = data?.description ?? data?.message ?? '';
        const match       = description.match(/(\d+)/);
        const addedQty    = match ? parseInt(match[1], 10) : null;
 
        if (requestedQty !== null) {
          document.dispatchEvent(
            new CustomEvent('cart-inventory:limit', {
              detail: { requested: requestedQty, added: addedQty },
            })
          );
        }
      }
    } catch { /* silent */ }
 
    return response;
  };
})();
 
// ─────────────────────────────────────────────────────────────────────────────
 
/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
 
  /** @type {AbortController | null} */
  #historyAbortController = null;
 
  /**
   * The mismatch data for the notice currently being shown.
   * Set when a limit event arrives, cleared when the notice is dismissed.
   * @type {{ requested: number, added: number | null } | null}
   */
  #activeNotice = null;
 
  /** MutationObserver that re-injects the notice after section re-renders. */
  #noticeObserver = null;
 
  /** Prevents the observer from triggering itself when we inject. */
  #isInjecting = false;
 
  /** @type {ReturnType<typeof setTimeout> | null} */
  #noticeDismissTimer = null;
 
  static #NOTICE_DISMISS_MS = 6000;
 
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('cart-inventory:limit', this.#handleInventoryLimit);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
 
    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }
 
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart-inventory:limit', this.#handleInventoryLimit);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#teardownNotice();
  }
 
  // ─── Inventory notice ─────────────────────────────────────────────────────
 
  #handleInventoryLimit = (event) => {
    // Store the data now; render after CartAddEvent fires and the drawer opens.
    this.#activeNotice = event.detail;
  };
 
  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
 
    if (this.#activeNotice) {
      // Small delay lets the drawer open and do its first render before we
      // inject. After that, the MutationObserver keeps the notice alive through
      // subsequent section re-renders.
      setTimeout(() => this.#startNoticeObserver(), 300);
    }
  };
 
  /**
   * Injects the notice element and starts a MutationObserver that re-injects
   * it whenever the section re-render wipes it from the DOM.
   */
  #startNoticeObserver() {
    if (!this.#activeNotice) return;
 
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;
 
    // Inject immediately.
    this.#injectNoticeElement(dialog);
 
    // Disconnect any previous observer.
    this.#noticeObserver?.disconnect();
 
    // Watch the entire dialog subtree. Whenever the notice disappears (because
    // the theme replaced the section HTML), re-inject it.
    this.#noticeObserver = new MutationObserver(() => {
      if (this.#isInjecting) return;
      if (!this.#activeNotice) return;
      if (!document.getElementById('cart-inventory-notice')) {
        this.#injectNoticeElement(dialog);
      }
    });
 
    this.#noticeObserver.observe(dialog, { childList: true, subtree: true });
 
    // Auto-dismiss after N seconds.
    clearTimeout(this.#noticeDismissTimer ?? undefined);
    this.#noticeDismissTimer = setTimeout(
      () => this.#teardownNotice(),
      CartDrawerComponent.#NOTICE_DISMISS_MS
    );
  }
 
  /**
   * Builds and prepends the notice element into the best available anchor
   * inside the drawer. Safe to call multiple times — always clears the
   * previous element first.
   *
   * @param {HTMLElement} dialog
   */
  #injectNoticeElement(dialog) {
    if (!this.#activeNotice) return;
 
    const anchor =
      dialog.querySelector('.cart-drawer__content') ??
      dialog.querySelector('.cart-drawer__body') ??
      dialog.querySelector('.cart-drawer__inner') ??
      dialog;
 
    // Remove any existing notice without triggering our observer guard.
    const existing = document.getElementById('cart-inventory-notice');
    if (existing) existing.remove();
 
    const { added } = this.#activeNotice;
    const piezas =
      added === null
        ? 'las piezas disponibles'
        : added === 1
        ? '1 pieza'
        : `${added} piezas`;
 
    const notice = document.createElement('div');
    notice.id = 'cart-inventory-notice';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.innerHTML = `
      <div class="cart-inv-notice">
        <span class="cart-inv-notice__icon" aria-hidden="true">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M5 2v3.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
            <circle cx="5" cy="7.5" r="0.75" fill="white"/>
          </svg>
        </span>
        <span class="cart-inv-notice__text">
          Debido a disponibilidad, solo se agregaron <strong>${piezas}</strong> al carrito.
        </span>
        <button class="cart-inv-notice__close" aria-label="Cerrar aviso">×</button>
      </div>
    `;
 
    notice
      .querySelector('.cart-inv-notice__close')
      ?.addEventListener('click', () => this.#teardownNotice());
 
    // Guard against the observer seeing our own injection as a mutation.
    this.#isInjecting = true;
    anchor.prepend(notice);
    this.#isInjecting = false;
 
    this.#injectStyles();
  }
 
  /**
   * Clears the active notice, disconnects the observer, and removes the element.
   */
  #teardownNotice() {
    this.#activeNotice = null;
    this.#noticeObserver?.disconnect();
    this.#noticeObserver = null;
    clearTimeout(this.#noticeDismissTimer ?? undefined);
    this.#noticeDismissTimer = null;
 
    const existing = document.getElementById('cart-inventory-notice');
    if (!existing) return;
 
    existing.querySelector('.cart-inv-notice')?.classList.add('cart-inv-notice--fade');
    setTimeout(() => existing.remove(), 320);
  }
 
  // ─── Styles ───────────────────────────────────────────────────────────────
 
  #injectStyles() {
    if (document.getElementById('cart-inv-notice-styles')) return;
 
    const style = document.createElement('style');
    style.id = 'cart-inv-notice-styles';
    style.textContent = `
      .cart-inv-notice {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        background: rgba(220, 80, 60, 0.10);
        border: 1px solid rgba(220, 80, 60, 0.35);
        border-radius: 8px;
        padding: 10px 12px;
        margin: 0 0 12px;
        animation: cartNoticeIn 0.25s ease forwards;
      }
      @keyframes cartNoticeIn {
        from { opacity: 0; transform: translateY(-6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .cart-inv-notice--fade {
        animation: cartNoticeFade 0.3s ease forwards;
      }
      @keyframes cartNoticeFade {
        to { opacity: 0; transform: translateY(-4px); }
      }
      .cart-inv-notice__icon {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        background: #dc503c;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 1px;
      }
      .cart-inv-notice__text {
        font-size: 13px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.9);
        flex: 1;
      }
      .cart-inv-notice__text strong {
        font-weight: 600;
        color: #fff;
      }
      .cart-inv-notice__close {
        background: none;
        border: none;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.45);
        font-size: 18px;
        line-height: 1;
        padding: 0;
        flex-shrink: 0;
        transition: color 0.15s;
      }
      .cart-inv-notice__close:hover {
        color: rgba(255, 255, 255, 0.85);
      }
    `;
    document.head.appendChild(style);
  }
 
  // ─── History helpers (unchanged) ──────────────────────────────────────────
 
  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;
 
    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }
 
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };
 
  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };
 
  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };
 
  // ─── Public API (unchanged) ───────────────────────────────────────────────
 
  open() {
    this.showDialog();
 
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }
 
  close() {
    this.closeDialog();
  }
 
  // ─── Sticky summary (unchanged) ───────────────────────────────────────────
 
  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;
 
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
 
    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }
 
    const drawerHeight  = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio         = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}
 
if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}